import { useEffect, useRef } from 'react';
import maplibregl, { type Map as MLMap, type StyleSpecification } from 'maplibre-gl';

export interface OverviewMarker {
  id: string;
  /** Typ markera — różnie kolorowany i z innym popupem. */
  type: 'intake' | 'client' | 'vehicle';
  lat: number;
  lng: number;
  /** Krótka etykieta nad markerem (np. nazwa ujęcia / nazwa klienta). */
  label: string;
  /** Treść HTML popupu — pokazywana po kliku. */
  popupHtml: string;
  /** Liczba dla badge'a klienta (ile ma ujęć), null/undefined dla zwykłych. */
  badge?: number;
  /**
   * Kolor pinezki (HEX). Nadpisuje kolor wynikający z `type` — używane przez mapę prac,
   * gdzie kolor niesie STAN robót u klienta, a nie rodzaj obiektu. Bez tego mapa prac
   * musiałaby mieć własny komponent mapy, a dwie mapy rozjeżdżają się przy pierwszej zmianie.
   */
  color?: string;
  /** Kolor tła badge'a (HEX). Domyślnie bursztynowy jak przy liczniku ujęć. */
  badgeColor?: string;
  /**
   * Kurs w stopniach (0 = północ, zgodnie ze wskazówkami zegara) dla markerów typu `vehicle`.
   * Podany — marker jest strzałką obróconą w tę stronę; pominięty (albo pojazd stoi) — kółkiem.
   * Pinezka nie niesie kierunku, a przy śledzeniu floty „w którą stronę jedzie" jest połową
   * informacji: sama kropka nie odróżnia auta wracającego od auta dojeżdżającego.
   */
  heading?: number;
  /** Marker rysowany bladziej (pojazd z nieaktualnym sygnałem) — punkt jest, ale nie jest świeży. */
  dimmed?: boolean;
}

interface Props {
  markers: OverviewMarker[];
  height?: string;
  /** Callback wywoływany gdy user kliknie marker. Argumenty: id i type. */
  onMarkerClick?: (id: string, type: 'intake' | 'client' | 'vehicle') => void;
  /**
   * Id markera, na którym mapa ma się wyśrodkować z otwartym popupem — obsługa deep-linków
   * („pokaż tego klienta na mapie"). Zamiast auto-fitu do wszystkich punktów robi flyTo do jednego.
   * Stosowane RAZ na wartość: późniejsza zmiana filtrów nie przerzuca widoku z powrotem.
   */
  focusId?: string | null;
  /**
   * Klik w link wewnątrz popupu. Bez tego `<a href="/klienci/1">` przeładowuje całą aplikację —
   * popupy są HTML-em poza Reactem, więc router sam ich nie przechwyci.
   */
  onNavigate?: (to: string) => void;
}

/**
 * Rysuje (albo przerysowuje) zawartość znacznika. Wydzielone z pętli, bo ten sam kod obsługuje
 * tworzenie markera i jego aktualizację w miejscu — pozycja pojazdu zmienia się co pół minuty
 * i przerysowanie jest wtedy zwykłą podmianą treści, a nie budową nowego markera.
 */
/** Marker żyjący na mapie wraz z ostatnio ustawioną treścią popupu (żeby nie podmieniać jej bez potrzeby). */
interface Wpis { id: string; marker: maplibregl.Marker; html: string }

function rysujPin(pin: HTMLElement, mk: OverviewMarker): void {
  const isClient = mk.type === 'client';
  const isVehicle = mk.type === 'vehicle';
  // Większy, niebieski pin dla klientów; mniejszy, pomarańczowy dla ujęć; pojazd pośrodku.
  const size = isVehicle ? 26 : isClient ? 32 : 22;
  const fill = mk.color ?? (isClient ? '#2563eb' : '#ea580c');
  // Pojazd rysujemy inaczej niż pinezkę: pinezka mówi „tu coś jest", a pojazd stoi w punkcie
  // i ma zwrot. Strzałka pokazuje kurs, kółko — postój (kurs po zatrzymaniu bywa zamrożony
  // na ostatnim, więc strzałka na postoju wskazywałaby kierunek, którego już nie ma).
  //
  // Strzałka jest rysowana NA PÓŁNOC i nieobracana tutaj: obrotem steruje MapLibre
  // (`rotation` + `rotationAlignment: 'map'`), dzięki czemu trzyma się terenu także wtedy,
  // gdy użytkownik obróci mapę kompasem. Obrót CSS-em kręciłby nią względem ekranu.
  pin.innerHTML = isVehicle
    ? (mk.heading != null
      ? `<svg width="${size}" height="${size}" viewBox="0 0 24 24">
           <circle cx="12" cy="12" r="11" fill="${fill}" stroke="white" stroke-width="2"/>
           <path d="M12 5 L16.5 17 L12 14.2 L7.5 17 Z" fill="white"/>
         </svg>`
      : `<svg width="${size}" height="${size}" viewBox="0 0 24 24">
           <circle cx="12" cy="12" r="11" fill="${fill}" stroke="white" stroke-width="2"/>
           <circle cx="12" cy="12" r="3.5" fill="white"/>
         </svg>`)
    : `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="${fill}" stroke="white" stroke-width="2">
         <path d="M12 2C8 2 5 5 5 9c0 5 7 13 7 13s7-8 7-13c0-4-3-7-7-7z"/>
         <circle cx="12" cy="9" r="3" fill="white"/>
       </svg>`;
  pin.style.cssText = 'filter:drop-shadow(0 2px 3px rgba(0,0,0,0.3));position:relative;'
    + (mk.dimmed ? 'opacity:0.55;' : '');
  // Badge z liczbą (ujęć albo prac) — dla klientów oraz wszędzie, gdzie podano wartość.
  if (mk.badge != null && mk.badge > 0) {
    const badge = document.createElement('span');
    badge.textContent = String(mk.badge);
    badge.style.cssText = `position:absolute;top:-4px;right:-6px;background:${mk.badgeColor ?? '#fbbf24'};color:#1f2937;`
      + 'font-size:10px;font-weight:700;border-radius:9px;padding:1px 5px;min-width:14px;text-align:center;'
      + 'border:1.5px solid white;line-height:1.1;';
    pin.appendChild(badge);
  }
}

/**
 * Prosta mapa MapLibre do prezentacji wielu punktów (ujęcia + klienci + pojazdy) na jednym widoku.
 * Auto-fit zoom do wszystkich markerów. Bez bbox-draw, parcel-layer, RAG-overlay — tylko czyste markery.
 */
export function OverviewMap({ markers, height = '70vh', onMarkerClick, focusId, onNavigate }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MLMap | null>(null);
  /** Markery z ich id — id jest potrzebne, by po przebudowie odnaleźć punkt deep-linku. */
  const markerRefsRef = useRef<Wpis[]>([]);
  const onClickRef = useRef(onMarkerClick);
  const onNavigateRef = useRef(onNavigate);
  /**
   * Czy użytkownik sam ruszył mapą. Auto-fit przy każdej zmianie markerów (a te zmieniają się przy
   * KAŻDYM przełączniku warstwy i filtrze) odrzucałby go z powrotem na całą Polskę — po dojechaniu
   * do powiatu kliknięcie filtra kasowałoby cały ten ruch.
   */
  const userMovedRef = useRef(false);
  /** Ostatnio zastosowany focusId — trzymany w refie, żeby nie odpalać flyTo przy każdym renderze. */
  const focusAppliedRef = useRef<string | null>(null);
  /** Podpis zbioru punktów, do którego ostatnio dopasowano widok — patrz auto-fit niżej. */
  const fitSignatureRef = useRef<string | null>(null);

  useEffect(() => { onClickRef.current = onMarkerClick; }, [onMarkerClick]);
  useEffect(() => { onNavigateRef.current = onNavigate; }, [onNavigate]);

  // Inicjalizacja mapy (raz)
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const style: StyleSpecification = {
      version: 8,
      sources: {
        osm: {
          type: 'raster',
          tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
          tileSize: 256,
          maxzoom: 19,
          attribution: '&copy; OpenStreetMap contributors',
        },
      },
      layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
    };

    // Centrum Polski jako fallback gdy brak markerów
    const m = new maplibregl.Map({
      container: containerRef.current,
      style,
      center: [19.1451, 51.9194],
      zoom: 6,
    });
    m.addControl(new maplibregl.NavigationControl(), 'top-right');
    m.addControl(new maplibregl.ScaleControl(), 'bottom-left');
    // Tylko ruch WYWOŁANY PRZEZ UŻYTKOWNIKA (ma originalEvent) — flyTo/fitBounds z kodu nie liczy się.
    const zapamietajRuch = (e: { originalEvent?: unknown }) => { if (e.originalEvent) userMovedRef.current = true; };
    m.on('dragstart', zapamietajRuch);
    m.on('zoomstart', zapamietajRuch);
    m.on('rotatestart', zapamietajRuch);
    mapRef.current = m;

    return () => {
      markerRefsRef.current.forEach(({ marker }) => marker.remove());
      markerRefsRef.current = [];
      m.remove();
      mapRef.current = null;
    };
  }, []);

  // Aktualizacja markerów + auto-fit
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;

    // Dane dochodzą falami (klienci → ujęcia → prace), a każda fala przebudowuje markery i kasuje
    // otwarty popup. Zapamiętujemy więc, czy popup deep-linku był otwarty, żeby go odtworzyć —
    // ale tylko wtedy: popup zamknięty ręcznie przez użytkownika ma zostać zamknięty.
    const focusPopupWasOpen = focusId
      ? markerRefsRef.current.find(x => x.id === focusId)?.marker.getPopup()?.isOpen() ?? false
      : false;

    // Markery są PRZERABIANE, nie budowane od nowa. Warstwa GPS przysyła nowe pozycje co kilkadziesiąt
    // sekund; gdyby każda taka fala kasowała i tworzyła wszystkie markery, otwarty popup zamykałby się
    // sam co pół minuty, a mapa mrugała. Marker o tym samym `id` zostaje na miejscu i tylko dostaje
    // nową pozycję, nowy rysunek i nową treść popupu.
    const poprzednie = new Map(markerRefsRef.current.map(x => [x.id, x]));
    const nowe: Wpis[] = [];
    /** Czy zmienił się SKŁAD zbioru — tylko wtedy kolejność rysowania wymaga poprawki (niżej). */
    let skladZmieniony = false;

    for (const mk of markers) {
      const isClient = mk.type === 'client';
      const isVehicle = mk.type === 'vehicle';
      const wpis = poprzednie.get(mk.id);
      let marker = wpis?.marker;

      if (wpis && marker) {
        poprzednie.delete(mk.id);
        marker.setLngLat([mk.lng, mk.lat]);
        rysujPin(marker.getElement().firstElementChild as HTMLElement, mk);
        // Kurs oddajemy MapLibre, a nie CSS-owemu `rotate`: znacznik obraca się wtedy RAZEM z mapą.
        // Przy obrocie widoku (kompas) strzałka rysowana w układzie ekranu wskazywałaby kierunek,
        // który już nie odpowiada terenowi — a to jedyna informacja, jaką ten znacznik niesie ponad punkt.
        if (isVehicle) marker.setRotation(mk.heading ?? 0);
        // setHTML na OTWARTYM popupie podmienia węzeł treści: gubi zaznaczony tekst i pozycję
        // przewijania. Przy warstwie odświeżanej co pół minuty robiłby to bez powodu każdemu,
        // kto akurat czyta popup klienta — więc podmieniamy tylko realnie zmienioną treść.
        if (wpis.html !== mk.popupHtml) marker.getPopup()?.setHTML(mk.popupHtml);
        nowe.push({ id: mk.id, marker, html: mk.popupHtml });
        continue;
      }
      {
        skladZmieniony = true;
        const el = document.createElement('div');
        el.style.cssText = 'cursor:pointer;display:flex;flex-direction:column;align-items:center;';
        const pin = document.createElement('div');
        rysujPin(pin, mk);
        el.appendChild(pin);

        const popup = new maplibregl.Popup({ offset: isVehicle ? 16 : isClient ? 32 : 22, maxWidth: '320px' })
          .setHTML(mk.popupHtml);

        // Linki w popupie idą przez router, nie przez pełne przeładowanie. Modyfikatory
        // (Ctrl/Cmd/środkowy przycisk) zostawiamy przeglądarce — „otwórz w nowej karcie" ma działać.
        //
        // JEDEN delegowany listener na kontenerze, nie po jednym na każdym <a> przy każdym otwarciu:
        // MapLibre przy zamknięciu popupu kasuje kontener, ale zachowuje węzeł treści z linkami, więc
        // podpinanie ich za każdym razem kumulowałoby wywołania (drugie otwarcie = dwie nawigacje).
        popup.on('open', () => {
          const root = popup.getElement() as HTMLElement | undefined;
          if (!root || root.dataset.navBound === '1') return;
          root.dataset.navBound = '1';
          root.addEventListener('click', ev => {
            const a = (ev.target as Element | null)?.closest?.('a[data-nav]') as HTMLAnchorElement | null;
            const cb = onNavigateRef.current;
            if (!a || !cb || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey || ev.button !== 0) return;
            ev.preventDefault();
            cb(a.getAttribute('href') ?? '');
          });
        });

        // Pinezka wskazuje punkt swoim czubkiem (kotwica u dołu), a znacznik pojazdu jest symetryczny
        // i musi stać środkiem NA pozycji — inaczej auto rysowałoby się o pół znacznika za wysoko.
        marker = new maplibregl.Marker({
          element: el,
          anchor: isVehicle ? 'center' : 'bottom',
          ...(isVehicle ? { rotation: mk.heading ?? 0, rotationAlignment: 'map' as const } : {}),
        })
          .setLngLat([mk.lng, mk.lat])
          .setPopup(popup)
          .addTo(m);

        el.addEventListener('click', () => {
          const cb = onClickRef.current;
          if (cb) cb(mk.id, mk.type);
        });

        nowe.push({ id: mk.id, marker, html: mk.popupHtml });
      }
    }

    // Zostały tylko te, których w nowym zbiorze już nie ma.
    if (poprzednie.size > 0) skladZmieniony = true;
    poprzednie.forEach(({ marker }) => marker.remove());
    markerRefsRef.current = nowe;

    // Kolejność rysowania = kolejność w DOM, a marker przerobiony w miejscu zostaje tam, gdzie był.
    // Bez tego dołożenie warstwy przestawiałoby stos: 32-pikselowy pin klienta potrafi przykryć
    // w całości 22-pikselowy pin ujęcia stojący w tym samym punkcie i odebrać mu klikalność,
    // a pojazd — który ma być NA WIERZCHU, bo mówi „co jest teraz" — wylądowałby pod nimi.
    // Poprawiamy tylko po zmianie składu; samo przesunięcie punktów kolejności nie rusza.
    if (skladZmieniony) {
      for (const { marker } of nowe) {
        const el = marker.getElement();
        el.parentNode?.appendChild(el);
      }
    }

    if (markers.length === 0) return;

    // Deep-link: jeden konkretny punkt zamiast auto-fitu. Tylko przy PIERWSZYM pojawieniu się
    // danego focusId — inaczej każda zmiana filtra odrzucałaby widok z powrotem na ten punkt.
    const target = focusId ? markers.find(mk => mk.id === focusId) : undefined;
    if (focusId && target) {
      const pierwszyRaz = focusAppliedRef.current !== focusId;
      if (pierwszyRaz) {
        focusAppliedRef.current = focusId;
        m.flyTo({ center: [target.lng, target.lat], zoom: 13, duration: 600 });
      }
      if (pierwszyRaz || focusPopupWasOpen) {
        const entry = markerRefsRef.current.find(x => x.id === focusId);
        if (entry && !entry.marker.getPopup()?.isOpen()) entry.marker.togglePopup();
      }
      // Dopóki deep-link jest aktywny, zmiana filtrów NIE przerzuca widoku na auto-fit —
      // inaczej „pokaż tego klienta" gubiłoby się przy pierwszym kliknięciu w filtr.
      return;
    }
    if (!focusId) focusAppliedRef.current = null;

    // Auto-fit tylko wtedy, gdy zmienił się ZBIÓR punktów (włączono warstwę, zadziałał filtr),
    // a nie gdy te same punkty jedynie się przesunęły. Bez tego warstwa GPS przerzucałaby widok
    // co każde odświeżenie pozycji: jadące auto zmienia współrzędne co pół minuty i mapa
    // dopasowywałaby się do niego w kółko, wyrywając użytkownikowi obraz sprzed chwili.
    const podpisZbioru = markers.map(mk => mk.id).join('|');
    if (fitSignatureRef.current === podpisZbioru) return;
    fitSignatureRef.current = podpisZbioru;

    // Auto-fit zoom — jeśli jeden marker, centruj z zoom 13; więcej → bounds
    if (userMovedRef.current) return;
    if (markers.length === 1) {
      m.flyTo({ center: [markers[0].lng, markers[0].lat], zoom: 13, duration: 600 });
    } else {
      const bounds = new maplibregl.LngLatBounds(
        [markers[0].lng, markers[0].lat],
        [markers[0].lng, markers[0].lat]
      );
      for (const mk of markers) bounds.extend([mk.lng, mk.lat]);
      m.fitBounds(bounds, { padding: 60, maxZoom: 12, duration: 600 });
    }
  }, [markers, focusId]);

  return <div ref={containerRef} style={{ width: '100%', height }}/>;
}
